// The Android app's device data on its way to the API. src/launch.ts keeps what the latest launch
// said in localStorage; once someone is signed in this sends it to POST /api/me/app-device, and
// again whenever it changes or another account signs in on the phone (that is how two accounts on
// one phone are noticed). A "sent" marker (a hash of the account and the data) stops repeats.
import type { AppLaunchData } from '@gaintplay/core/app-launch';
import { api, ApiError } from './api';

// Written by src/launch.ts. Repeated here rather than imported: importing that file would pull it
// (and its MD5) into the main bundle, and it must stay a small file of its own.
const LAUNCH_STORE = 'gp_app_launch';
const APP_SEEN = 'gp_app';
/** launch.ts fires this on window when a launch's data has been stored. */
export const LAUNCH_EVENT = 'gp:app-launch';
/** Fired on window (detail: coins) when a device report came back with the app bonus. */
export const APP_BONUS_EVENT = 'gp:app-bonus';
const SENT = 'gp_app_sent';
/** Placeholders some Android builds put where a value is missing. */
const EMPTY = new Set(['', 'null', 'undefined', 'unknown', 'n/a']);

function read<T>(key: string): T | null {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}

/** Has the Android app opened the site in this browser? (The Account page's Notifications row.) */
export function isAppDevice(): boolean {
  try {
    return localStorage.getItem(APP_SEEN) === '1';
  } catch {
    return false;
  }
}

/** What the latest verified launch said, or null. */
export function appLaunchData(): AppLaunchData | null {
  const stored = read<{ v: number; data: AppLaunchData }>(LAUNCH_STORE);
  return stored?.v === 1 && stored.data && typeof stored.data === 'object' ? stored.data : null;
}

/** Has this phone given the app a push token? */
export function hasPushToken(): boolean {
  return !!appLaunchData()?.fcmToken;
}

async function sha256(text: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(d), (b) => b.toString(16).padStart(2, '0')).join('');
}

let inflight: Promise<void> | null = null;

/** Send the app's device data for this account if it hasn't been sent as it is now. Never throws. */
export function syncAppDevice(userId: string): Promise<void> {
  inflight ??= send(userId).finally(() => (inflight = null));
  return inflight;
}

async function send(userId: string): Promise<void> {
  const data = appLaunchData();
  if (!data) return;
  const body: Record<string, unknown> = { keyVerified: true };
  for (const [k, v] of Object.entries(data))
    if (typeof v === 'boolean' || (typeof v === 'string' && !EMPTY.has(v.trim().toLowerCase())))
      body[k] = v;
  if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true)
    body.gpc = true;
  let marker: string;
  try {
    marker = await sha256(`${userId}|${JSON.stringify(body)}`);
    if (localStorage.getItem(SENT) === marker) return;
  } catch {
    return; // no storage or no WebCrypto: nothing to compare against, so don't resend every page
  }
  try {
    const r = await api<{ appBonus?: number }>('/me/app-device', { body });
    if (r.appBonus) window.dispatchEvent(new CustomEvent(APP_BONUS_EVENT, { detail: r.appBonus }));
  } catch (e) {
    // Refused as malformed: sending the same again won't help. Anything else: next page load.
    if (!(e instanceof ApiError && e.status === 400)) return;
  }
  try {
    localStorage.setItem(SENT, marker);
  } catch {
    /* storage blocked */
  }
}
